import type { QuestionType } from "@examing/types";
import { htmlIsEmpty } from "modules/editor/utils";

export interface QuestionValidationInput {
  id?: string;
  subjectId?: string;
  type: QuestionType | string;
  prompt?: string;
  questionText?: string;
  options?: Array<{
    id: string;
    value?: string;
    text?: string;
    isCorrect?: boolean;
  }>;
  matchPairs?: Array<{ key?: string; value?: string }>;
  matchingOptions?: string[];
  correctAnswer?: unknown;
  answerMode?: "exact" | "manual";
  acceptedAnswers?: string[];
  maxWords?: number | null;
  blanks?: Array<{ id: string; acceptedAnswers?: string[] }>;
  reorderItems?: Array<{ id: string; content?: string }>;
  correctOrder?: string[];
  passageText?: string;
  subQuestions?: unknown[];
  points?: number;
}
export interface QuestionValidationIssue {
  path: string;
  code: string;
  message: string;
}
export interface QuestionValidationResult {
  isValid: boolean;
  error?: string;
  issues: QuestionValidationIssue[];
}
const text = (value: unknown): value is string =>
  typeof value === "string" && !htmlIsEmpty(value);
const unique = (ids: unknown[]) =>
  ids.every(text) && new Set(ids).size === ids.length;
const coreTypes = [
  "MULTIPLE_CHOICE",
  "MULTIPLE_RESPONSE",
  "TRUE_FALSE",
  "SHORT_ANSWER",
  "MATCHING",
  "FILL_IN_BLANKS",
];
const aliases = (q: QuestionValidationInput): unknown[] =>
  q.acceptedAnswers ??
  (Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]);
const validAnswers = (answers: unknown[], max?: number | null) =>
  answers.length > 0 &&
  answers.every(text) &&
  (!max ||
    answers.some(
      (answer) =>
        typeof answer === "string" && answer.trim().split(/\s+/).length <= max,
    ));

export function validateQuestion(
  q: QuestionValidationInput,
  reading = false,
): QuestionValidationResult {
  const issues: QuestionValidationIssue[] = [];
  const add = (path: string, code: string, message: string) => issues.push({ path, code, message });
  const result = (): QuestionValidationResult => ({ isValid: issues.length === 0, error: issues[0]?.message, issues });
  if (!q || typeof q !== "object") {
    add("type", "missing_question", "Savol ma'lumotlari mavjud emas");
    return result();
  }
  if (q.subjectId !== undefined && !text(q.subjectId))
    add("subjectId", "subject", "Fan tanlanishi shart");
  if (q.points !== undefined && q.points !== null && (!Number.isFinite(q.points) || q.points <= 0))
    add("points", "points", "Ball musbat son bo‘lishi kerak");
  if (
    q.maxWords !== undefined &&
    q.maxWords !== null &&
    (q.maxWords as unknown) !== "" &&
    (!Number.isInteger(Number(q.maxWords)) ||
      Number(q.maxWords) < 1 ||
      Number(q.maxWords) > 1000)
  )
    add("maxWords", "word_limit", "So‘zlar chegarasi 1–1000 oralig‘ida bo‘lishi kerak");
  if (
    q.answerMode !== undefined &&
    q.answerMode !== null &&
    (q.answerMode as unknown) !== "" &&
    !["exact", "manual"].includes(q.answerMode)
  )
    add("answerMode", "answer_mode", "Baholash usuli noto‘g‘ri");
  const prompt = (reading ? (q.questionText ?? q.prompt) : q.prompt) ?? "";
  if (q.type === "PASSAGE") {
    if (!text(q.passageText)) add("passageText", "passage", "Matn (Passage) matni kiritilishi shart");
    const children = q.subQuestions as QuestionValidationInput[] | undefined;
    if (!children?.length) add("subQuestions", "children", "Matn ostida kamida bitta savol bo‘lishi shart");
    else {
      if (!unique(children.map((child) => child?.id)))
        add("subQuestions", "child_ids", "Ichki savol identifikatorlari takrorlanmasligi kerak");
      for (const [index, child] of children.entries()) {
        if (!child || !coreTypes.includes(child.type)) {
          add(`subQuestions.${index}.type`, "reading_type", `${index + 1}-kichik savol turi reading uchun mos emas`);
          continue;
        }
        for (const issue of validateQuestion(child, true).issues) {
          issues.push({ ...issue, path: `subQuestions.${index}.${issue.path}`, message: `${index + 1}-kichik savol: ${issue.message}` });
        }
      }
    }
    return result();
  }
  if (!text(prompt)) add("prompt", "prompt", "Savol matni kiritilishi shart");
  switch (q.type) {
    case "MULTIPLE_CHOICE":
    case "MULTIPLE_RESPONSE":
    case "TRUE_FALSE": {
      const options = q.options ?? [];
      if (options.length < 2 || (q.type === "TRUE_FALSE" && options.length !== 2))
        add("options", "options_count", "Kamida 2 ta javob varianti kiritilishi shart");
      if (!unique(options.map((option) => option.id)))
        add("options", "option_ids", "Variant identifikatorlari to‘liq va takrorlanmas bo‘lishi kerak");
      options.forEach((option, index) => {
        if (!text(option.value ?? option.text)) add(`options.${index}.value`, "option_text", "Variant matni kiritilishi shart");
      });
      const marked = options.filter((option) => option.isCorrect === true).map((option) => option.id);
      const key = q.correctAnswer === undefined || q.correctAnswer === ""
        ? q.type === "MULTIPLE_RESPONSE" ? marked : marked.length === 1 ? marked[0] : undefined
        : q.correctAnswer;
      const selected = q.type === "MULTIPLE_RESPONSE" ? Array.isArray(key) ? key : [] : typeof key === "string" ? [key] : [];
      if (!selected.length || !unique(selected) || !selected.every((id) => options.some((option) => option.id === id)))
        add("correctAnswer", "correct_option", "To‘g‘ri javob varianti tanlanishi shart");
      break;
    }
    case "SHORT_ANSWER":
      if (q.answerMode === "exact" && !validAnswers(aliases(q), q.maxWords))
        add("acceptedAnswers", "accepted_answers", "So‘z chegarasiga mos to‘g‘ri javob kiriting");
      break;
    case "OPEN_ENDED":
      break;
    case "SPR": {
      // SAT grid-in keys: numbers, decimals or fractions (the API applies the grid rules).
      const keys = (q.acceptedAnswers ?? []).map((k) => String(k).trim()).filter(Boolean);
      if (!keys.length) add("acceptedAnswers", "spr_keys", "Kamida bitta to‘g‘ri javob kiritilishi shart");
      else if (!keys.every((k) => /^-?(?:\d+\/\d+|\d+(?:\.\d*)?|\.\d+)$/.test(k)))
        add("acceptedAnswers", "spr_number", "Javoblar son, o‘nli kasr yoki 2/3 kabi kasr bo‘lishi kerak");
      break;
    }
    case "MATCHING": {
      const pairs = q.matchPairs ?? [];
      if (pairs.length < 2) add("matchPairs", "pairs_count", "Kamida 2 ta moslik juftligi kiritilishi shart");
      pairs.forEach((pair, index) => {
        if (!text(pair.key)) add(`matchPairs.${index}.key`, "pair_key", "Chap tomondagi matnni kiriting");
        else if (pairs.some((other, otherIndex) => otherIndex !== index && other.key === pair.key))
          add(`matchPairs.${index}.key`, "pair_duplicate", "Chap tomonlar takrorlanmasligi kerak");
        if (!text(pair.value)) add(`matchPairs.${index}.value`, "pair_value", "Mos to‘g‘ri javobni kiriting");
      });
      break;
    }
    case "FILL_IN_BLANKS": {
      const blanks = q.blanks ?? [];
      if (reading && !blanks.length && validAnswers(aliases(q), q.maxWords)) break;
      if (!blanks.length) add("blanks", "blanks_count", "Kamida bitta bo‘sh joy (blank) kiritilishi shart");
      else {
        const placeholders = Array.from(prompt.matchAll(/\{\{([^}]+)\}\}/g), (match) => match[1]);
        if (!unique(blanks.map((blank) => blank.id)) || placeholders.length !== blanks.length || !unique(placeholders) || !placeholders.every((id) => blanks.some((blank) => blank.id === id)))
          add("prompt", "blank_markers", "Matndagi bo‘sh joylar va javob kalitlari mos kelishi kerak");
        blanks.forEach((blank, index) => {
          if (!validAnswers(blank.acceptedAnswers ?? [], q.maxWords))
            add(`blanks.${index}.acceptedAnswers`, "accepted_answers", "So‘z chegarasiga mos to‘g‘ri javob kiriting");
        });
      }
      break;
    }
    case "REORDER": {
      const items = q.reorderItems ?? [], order = q.correctOrder ?? [];
      if (items.length < 2) add("reorderItems", "items_count", "Kamida 2 ta element kiritilishi shart");
      if (!unique(items.map((item) => item.id))) add("reorderItems", "item_ids", "Element identifikatorlari to‘liq va takrorlanmas bo‘lishi kerak");
      items.forEach((item, index) => {
        if (!text(item.content)) add(`reorderItems.${index}.content`, "item_text", "Element matni kiritilishi shart");
      });
      if (order.length !== items.length || !unique(order) || !order.every((id) => items.some((item) => item.id === id)))
        add("correctOrder", "order", "Barcha elementlarning to‘g‘ri tartibi belgilanishi shart");
      break;
    }
    default:
      add("type", "unsupported_type", "Bu savol turi qo‘llab-quvvatlanmaydi");
  }
  return result();
}
